import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {AgGridReact} from "ag-grid-react";
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams,} from "ag-grid-enterprise";
import {useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState} from "recoil";
import {FilterDef} from "../../../constants/FinanceModels";
import {formatCurrency, formatDate, formatDateWithoutTime} from "../../../utils/FormatUtils";
import {getLocalGridState, setLocalGridState,} from "../../../utils/GridUtils";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../util/component/EmptyState";
import ColouredChip from "../../../component/ColouredChip";
import {Button, IconButton, Stack, Tooltip} from "@mui/material";
import {AddRounded, DescriptionRounded, DownloadRounded, UploadRounded} from "@mui/icons-material";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import {useNavigate} from "react-router-dom";
import useToggle from "../hooks/useToggle";
import {NumericSummary} from "../../../component/NumericSummary";
import {STATE_ACTIVE_FILTERS, STATE_OUTCOMES_UPLOADING, STATE_TABLE_UPDATING} from "../constants/ReportRecoil";
import {getSummaryFields, handleDownload, TRANSACTION_STATUSES} from "../constants/ReportConstants";
import {useAmexReportsRepository} from "../../../api/repositories/AmexReportsRepository";
import {STATE_SUMMARY_UPDATING} from "../../../constants/FinanceRecoil";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import ReportsModal from "../sections/ReportsModal";
import CreateReportModal from "../sections/CreateReportModal";
import {useAgGridLocale, useHasScope} from "../../../../../config/globalConfig";
import {
    AMEXSOF_CREATE_TRANSACTION_REPORT,
    AMEXSOF_DOWNLOAD_REPORT,
    AMEXSOF_IMPORT_OUTCOMES,
    AMEXSOF_VIEW_TRANSACTION_REPORT_DETAIL
} from "../../../utils/FinanceScope";
import ResultsFile from "../sections/ResultsFile";


const ReportTable = () => {
    const UNIQUE_ID = "fnn-sdd-amex-report";

    const {t} = useTranslation("finance", {keyPrefix: "payments.amex.transactions"});
    const {getReports, downloadReport, reportSummary, uploadFile} = useAmexReportsRepository();
    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
    const [updating, setUpdating] = useRecoilState(STATE_TABLE_UPDATING);
    const [currentRow, setCurrentRow] = useState<any>();
    const [showDownload, toggleDownload] = useToggle();
    const agGridLocale = useAgGridLocale();
    const [showCreateReport, toggleCreateReport] = useToggle();
    const {dispatchGlobalData} = useContext(GlobalContext);
    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const hasScope = useHasScope;
    const [openFile, setOpenFile] = useState(false);


    const gridRef = useRef<AgGridReact>(null);

    const columnDefs: ColDef[] = [
        {
            field: 'name', headerName: t('table.reportName'), headerTooltip: t('table.reportName'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: "createdDate",
            headerName: t("table.creationDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: "modifiedDate",
            headerName: t("table.modificationDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: 'transactionCount',
            headerName: t('table.transactionNumber'),
            headerTooltip: t('table.transactionNumber'),
            minWidth: 250,
            unSortIcon: true
        },
        {
            field: 'transactionTotalAmount', headerName: t('table.totalAmount'), headerTooltip: t('table.totalAmount'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatCurrency(params.value, 'EUR', 0.01)
        },
        {
            field: "paymentDate",
            headerName: t("table.paymentDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDateWithoutTime(params.value),
        },
        {
            field: "status",
            headerName: t("table.status"),
            minWidth: 100,
            unSortIcon: true,
            sortable: true,
            pinned: "right",
            lockPosition: "right",
            lockVisible: true,
            lockPinned: true,
            cellRendererSelector: (params) => {
                let status: string = params.data.status;
                let color = TRANSACTION_STATUSES.find(s => s.value === status)?.color?.split(".")[0];
                let tooltipText: string = params.data.errorReason;
                if (!color || color === "neutral") color = "default";
                return {
                    component: () => (
                        <ColouredChip name={status} color={color} tooltip={tooltipText}/>
                    ),
                };
            },
        },
        {
            field: "legalEntity",
            headerName: t("table.legalEntity"),
            minWidth: 180,
            unSortIcon: true,
            sortable: true,
        },
        {
            field: "dealer",
            headerName: t("table.dealer"),
            minWidth: 180,
            unSortIcon: true,
            sortable: true,
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                const {status, paymentDate, id, name} = params.data;
                const isVisible = status === 'ERROR';
                const downloadElement = () => {
                    setCurrentRow(params.data);
                    if (new Date(paymentDate).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)) {
                        toggleDownload();
                    } else {
                        download(id, name);
                    }
                }
                return {
                    component: () =>

                        <FinanceDynamicActions>
                            {hasScope(AMEXSOF_VIEW_TRANSACTION_REPORT_DETAIL) &&
                                <Tooltip arrow placement={"top"} title={t("table.actions.details")}>
                                    <IconButton color="accent" disabled={isVisible} onClick={() => {
                                        window.open(`/finance/payments/amex/transactions/wizard/${id}/${name}`, "_blank");
                                    }}>
                                        <DescriptionRounded/>
                                    </IconButton>
                                </Tooltip>
                            }
                            {hasScope(AMEXSOF_DOWNLOAD_REPORT) &&
                                <Tooltip arrow placement={"top"} title={t("table.actions.download")}>
                                    <IconButton color="accent" disabled={isVisible} onClick={() => {
                                        paymentDate ? downloadElement() : (() => {
                                        })();
                                    }}>
                                        <DownloadRounded/>
                                    </IconButton>
                                </Tooltip>
                            }
                        </FinanceDynamicActions>
                };
            },
            suppressFiltersToolPanel: true
        }
    ];

    const toolbar = {
        renderInfo: () => (
            <NumericSummary query={() => reportSummary(filterDefs)} fields={getSummaryFields()}
                            i18nPath={"payments.amex.transactions.reports.counters"}/>
        ),
        renderActions: () => (
            <Stack direction={'row'} spacing={2}>
                {hasScope(AMEXSOF_IMPORT_OUTCOMES) &&
                    <Button color={"accent"} style={{minWidth: '180px'}} variant="contained" component="label"
                            startIcon={<UploadRounded/>}
                        onClick={() => setOpenFile(true)}>
                        {t('buttons.upload')}
                    </Button>
                }
                {hasScope(AMEXSOF_CREATE_TRANSACTION_REPORT) &&
                    <Button fullWidth color={"accent"} variant="contained" component="label"
                            startIcon={<AddRounded/>}
                            onClick={() => {
                                toggleCreateReport();
                            }}>
                        {t('buttons.create')}
                    </Button>
                }
            </Stack>
        ),
    };

    async function download(id: string, reportName: string) {
        try {
            const response = await downloadReport(id, false);
            handleDownload(reportName, response.result);
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("snackbar.success-download"),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true,
                    },
                },
            });
            setUpdating(true);
        } catch (error) {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("snackbar.error-download"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    },
                },
            });
        }
    }

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data["id"],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
            headerCheckboxSelection: true
        },
        noRowsOverlayComponentParams: {
            title: t("table.no-rows.title"),
            content: t("table.no-rows.content")
        },
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(
            createDatasource(filterDefs, gridRef.current.api)
        );
        setSummaryUpdating(true);
    }, [filterDefs]);

    useEffect(() => {
    }, [currentRow]);

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(
                createDatasource(filterDefs, gridRef.current.api)
            );
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating]);

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].colId;
                let order =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].sort.toUpperCase();
                getReports(startRow, endRow - startRow, sort, order, filterDefs)
                    .then((response) => {
                        let result = response.result;

                        if (result["total-amount"] === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result["total-amount"],
                        });
                    })
                    .catch(() => params.fail());
            },
        };
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState(),
        });
    }

    function formatPaymentDate(value: string): string {
        const date = new Date(value);
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const formattedDate = `${dd}/${mm}/${yyyy}`;
        return formattedDate;
    }

    return (
        <>

            {showDownload &&
                <ReportsModal
                    open={showDownload}
                    form={currentRow}
                    title={t("modal.confirmDateTitle")}
                    body={t("modal.confirmDateBody", {paymentDate: currentRow?.paymentDate ? formatPaymentDate(currentRow.paymentDate) : ""})}
                    leftButton={t("modal.actionButtons.discard")}
                    leftFunction={toggleDownload}
                    rightButton={t("modal.actionButtons.download")}
                    onClose={toggleDownload}

                />
            }

            {showCreateReport &&
                <CreateReportModal
                    open={showCreateReport}
                    title={t("modal.confirmCreateReportTitle")}
                    body={t("modal.confirmCreateReportBody")}
                    leftButton={t("modal.actionButtons.discard")}
                    leftFunction={toggleCreateReport}
                    rightButton={t("modal.actionButtons.confirm")}
                    onClose={toggleCreateReport}

                />
            }

            <AgGridCustomTable
                toolbar={toolbar}
                rowModelType="serverSide"
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows}
                serverSideInfiniteScroll
                gridRef={gridRef}
                columnDefs={columnDefs}
                gridOptions={gridOptions}
                rowSelection={"multiple"}
                suppressRowClickSelection
                noRowsOverlayComponent={noRowsOverlayComponent}
                onGridReady={() => {
                    gridRef.current!.api.setServerSideDatasource(
                        createDatasource(filterDefs, gridRef.current!.api)
                    );

                    let gridState = getLocalGridState(UNIQUE_ID).cols;
                    if (gridState !== undefined) {
                        gridRef.current!.columnApi.applyColumnState({
                            state: gridState,
                            applyOrder: true,
                        });
                    }
                }}
                onColumnMoved={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnPinned={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnResized={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onSortChanged={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onPaginationChanged={() => {
                    if (gridRef.current!.api) {
                        setLocalGridState(UNIQUE_ID, {
                            rows: gridRef.current!.api?.paginationGetPageSize(),
                            cols: getLocalGridState(UNIQUE_ID).cols,
                        });
                    }
                }}
            ></AgGridCustomTable>
            <ResultsFile open={openFile}
                         onClose={() => {
                             setOpenFile(false)
                         }}
            />

        </>
    );
};

export default ReportTable;